"use client";

import * as React from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Database, Search, X, Loader2, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { UseChatHelpers } from "@ai-sdk/react";
import {
  loadDataCatalog,
  scoreDataSource,
  CATEGORY_ICON,
  type DataCatalog,
  type DataSource,
  type RequestedDataSource,
} from "@/lib/data-catalog";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { useIsMobile } from "@/hooks/use-is-mobile";
import {
  Funnel,
  CurrencyBtc,
  ChartLineUp,
  TrendUp,
  TreeStructure,
  Calculator,
  Bell,
} from "phosphor-react";
import type { IconProps } from "phosphor-react";

// Tiny logger so we can see open/close + which path (popover/sheet) is
// running, directly in the browser console. Turn off by setting
// `localStorage.setItem('xynth_ds_log', '0')` in DevTools.
const dsLog = (...args: unknown[]) => {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem("xynth_ds_log") === "0") return;
  } catch {
    /* ignore */
  }
  // eslint-disable-next-line no-console
  console.log("[DataSelector]", ...args);
};

const ICON_MAP: Record<string, React.ComponentType<IconProps>> = {
  Funnel,
  CurrencyBtc,
  ChartLineUp,
  TrendUp,
  TreeStructure,
  Calculator,
  Bell,
};

const MAX_SELECTED = 8;
// Filter chips — only the 5 user-visible categories, in display order.
const FILTER_CHIPS = ["Backtesting", "Screeners", "Options", "Stocks", "Fundamentals"] as const;

export function DataSelector({
  status: _status,
  selected,
  onToggle,
  onRemove,
  compact = false,
}: {
  status: UseChatHelpers["status"];
  selected: Array<RequestedDataSource>;
  onToggle: (source: DataSource) => void;
  onRemove: (id: string) => void;
  compact?: boolean;
}) {
  void _status;
  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [activeTags, setActiveTags] = React.useState<Set<string>>(new Set());
  const [catalog, setCatalog] = React.useState<DataCatalog | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const isMobile = useIsMobile();

  React.useEffect(() => {
    if (!open || catalog) return;
    let cancelled = false;
    setLoadError(null);
    loadDataCatalog()
      .then((c) => {
        if (!cancelled) setCatalog(c);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e?.message || "Failed to load data catalog");
      });
    return () => {
      cancelled = true;
    };
  }, [open, catalog]);

  const toggleTag = React.useCallback((tag: string) => {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }, []);

  // Group + score + tag-filter all sources for current query.
  const grouped = React.useMemo(() => {
    if (!catalog) return [] as Array<{ category: string; items: DataSource[] }>;
    const wantedTags = activeTags;

    const scored = catalog.sources
      .filter((s) => {
        if (wantedTags.size === 0) return true;
        // Multi-select AND: every active tag must be present on the source.
        for (const t of wantedTags) if (!s.tags.includes(t)) return false;
        return true;
      })
      .map((src) => ({ src, score: scoreDataSource(src, query) }))
      .filter((x) => x.score > 0);

    const byCat = new Map<string, typeof scored>();
    for (const item of scored) {
      const arr = byCat.get(item.src.category) ?? [];
      arr.push(item);
      byCat.set(item.src.category, arr);
    }
    for (const [cat, arr] of byCat) {
      arr.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.src.name.localeCompare(b.src.name);
      });
      byCat.set(cat, arr);
    }

    return catalog.categories
      .map((c) => ({ category: c, items: (byCat.get(c) ?? []).map((x) => x.src) }))
      .filter((c) => c.items.length > 0);
  }, [catalog, query, activeTags]);

  const totalMatches = React.useMemo(
    () => grouped.reduce((n, g) => n + g.items.length, 0),
    [grouped]
  );
  const totalSources = catalog?.sources.length ?? 0;

  const trigger = compact ? (
    isMobile ? (
      // On mobile, skip the Tooltip wrapper — it confuses Radix's
      // asChild slot composition AND tooltips don't really work on
      // touch devices anyway.
      <button
        type="button"
        aria-label="Add data sources"
        onClick={() => dsLog("trigger tapped (mobile compact)")}
        className={cn(
          "flex items-center justify-center p-2 rounded-md transition-all duration-150",
          selected.length > 0
            ? "bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20"
            : "text-zinc-400 hover:text-white hover:bg-zinc-800/60"
        )}
      >
        <Database size={16} />
        {selected.length > 0 && (
          <span className="ml-0.5 flex items-center justify-center h-3.5 min-w-[14px] rounded-sm bg-emerald-400 text-[9px] font-bold leading-none text-zinc-950">
            {selected.length}
          </span>
        )}
      </button>
    ) : (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="Add data sources"
            onClick={() => dsLog("trigger tapped (compact)")}
            className={cn(
              "flex items-center justify-center p-1.5 rounded-md transition-all duration-150",
              selected.length > 0
                ? "bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20"
                : "text-zinc-400 hover:text-white hover:bg-zinc-800/60"
            )}
          >
            <Database size={14} />
            {selected.length > 0 && (
              <span className="ml-0.5 flex items-center justify-center h-3.5 min-w-[14px] rounded-sm bg-emerald-400 text-[9px] font-bold leading-none text-zinc-950">
                {selected.length}
              </span>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs px-2 py-1">
          Add data sources
        </TooltipContent>
      </Tooltip>
    )
  ) : (
    <button
      type="button"
      aria-label="Add data sources"
      onClick={() => dsLog("trigger tapped (full)")}
      className={cn(
        "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition-all duration-150 border",
        selected.length > 0
          ? "bg-emerald-400/10 border-emerald-400/30 text-emerald-300 hover:bg-emerald-400/20"
          : "border-transparent text-zinc-300 hover:text-white hover:bg-zinc-800/60"
      )}
    >
      <Database size={14} />
      <span className="text-[11px] font-semibold tracking-wide leading-none whitespace-nowrap">
        Data{selected.length > 0 ? ` (${selected.length})` : " +"}
      </span>
    </button>
  );

  // Log open/close transitions + viewport info so we can debug "modal
  // doesn't open" issues from a phone via remote debugging or shared
  // console screenshots.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    dsLog("open=", open, "isMobile=", isMobile, "vw=", window.innerWidth, "vh=", window.innerHeight);
  }, [open, isMobile]);

  return (
    <div className="flex items-center gap-1">
      {isMobile ? (
        // ===== MOBILE: centered floating modal via Radix Dialog =====
        // Dialog (unlike Popover) is NOT anchored to its trigger, so we
        // can position the panel anywhere with a plain className — no
        // fighting Radix's inline transform styles.
        <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
          <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay
              className={cn(
                // Real backdrop blur on the page behind, plus a soft dim
                // so the modal floats and stays the center of attention.
                "fixed inset-0 z-[60] bg-black/55 backdrop-blur-md",
                "data-[state=open]:animate-in data-[state=open]:fade-in-0",
                "data-[state=closed]:animate-out data-[state=closed]:fade-out-0"
              )}
            />
            <DialogPrimitive.Content
              aria-label="Data sources"
              className={cn(
                // Centered floating card. 92% width, ~72% height, with
                // breathing room top + bottom so the page-blur is visible
                // all around the modal — that's what gives the "chic
                // expensive" feel.
                "fixed left-1/2 top-1/2 z-[70]",
                "w-[92vw] max-w-[540px]",
                "h-[72vh] max-h-[640px]",
                "-translate-x-1/2 -translate-y-1/2",
                "flex flex-col",
                "rounded-2xl",
                "border border-zinc-800 bg-zinc-950 shadow-2xl",
                "outline-none overflow-hidden",
                "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
                "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
              )}
              onOpenAutoFocus={(e) => {
                // Don't autofocus the search input — keeps the keyboard
                // down so the user can see the picker first.
                e.preventDefault();
              }}
            >
              {/* Dedicated header strip — title on the left, X on the
                  right. Reserved its own row so the close button can
                  never overlap any other content. */}
              <div className="flex items-center justify-between px-4 pt-3 pb-2 shrink-0 border-b border-zinc-800/70 bg-zinc-950">
                <DialogPrimitive.Title className="text-[13px] font-semibold tracking-tight text-white">
                  Data sources
                </DialogPrimitive.Title>
                <DialogPrimitive.Close
                  className="inline-flex items-center justify-center size-8 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-800 active:bg-zinc-800 transition-colors -mr-1.5"
                  aria-label="Close"
                >
                  <X className="size-4" />
                </DialogPrimitive.Close>
              </div>
              <DataSelectorBody
                isMobile
                query={query}
                setQuery={setQuery}
                totalSources={totalSources}
                activeTags={activeTags}
                setActiveTags={setActiveTags}
                toggleTag={toggleTag}
                grouped={grouped}
                totalMatches={totalMatches}
                catalog={catalog}
                loadError={loadError}
                selected={selected}
                onToggle={onToggle}
              />
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      ) : (
        // ===== DESKTOP: anchored popover =====
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent
            align="start"
            side="bottom"
            sideOffset={12}
            collisionPadding={24}
            className={cn(
              "flex flex-col",
              "max-h-[min(640px,var(--radix-popover-content-available-height))]",
              "w-[min(540px,var(--radix-popover-content-available-width))]",
              "max-w-[92vw] p-0 border-zinc-800 bg-zinc-950 shadow-2xl rounded-[10px] overflow-hidden"
            )}
          >
            <DataSelectorBody
              isMobile={false}
              query={query}
              setQuery={setQuery}
              totalSources={totalSources}
              activeTags={activeTags}
              setActiveTags={setActiveTags}
              toggleTag={toggleTag}
              grouped={grouped}
              totalMatches={totalMatches}
              catalog={catalog}
              loadError={loadError}
              selected={selected}
              onToggle={onToggle}
            />
          </PopoverContent>
        </Popover>
      )}

      {/* Selected chips beside the trigger. On desktop: inline horizontal
          row. On mobile: hidden here because they wrap to a separate row
          below the toolbar via parent CSS — see SelectedDataChips. */}
      {!compact && !isMobile && selected.length > 0 && (
        <div className="flex items-center gap-1.5 overflow-x-auto whitespace-nowrap max-w-[40vw] md:max-w-[24rem]">
          {selected.map((src) => (
            <span
              key={src.id}
              className="bg-emerald-400/10 border border-emerald-400/30 text-emerald-300 h-6 px-2 text-[11px] font-medium inline-flex items-center gap-1 rounded-md"
            >
              <span>{src.name}</span>
              <button
                type="button"
                onClick={() => onRemove(src.id)}
                className="ml-0.5 inline-flex items-center justify-center size-4 rounded-sm hover:bg-emerald-400/25"
                aria-label={`Remove ${src.name}`}
              >
                <X className="size-3 text-emerald-300" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Shared body for both the desktop popover and the mobile bottom-sheet
 *  dialog — header, filter chips, list, footer. */
function DataSelectorBody({
  isMobile,
  query,
  setQuery,
  totalSources,
  activeTags,
  setActiveTags,
  toggleTag,
  grouped,
  totalMatches,
  catalog,
  loadError,
  selected,
  onToggle,
}: {
  isMobile: boolean;
  query: string;
  setQuery: (v: string) => void;
  totalSources: number;
  activeTags: Set<string>;
  setActiveTags: React.Dispatch<React.SetStateAction<Set<string>>>;
  toggleTag: (tag: string) => void;
  grouped: Array<{ category: string; items: DataSource[] }>;
  totalMatches: number;
  catalog: DataCatalog | null;
  loadError: string | null;
  selected: Array<RequestedDataSource>;
  onToggle: (source: DataSource) => void;
}) {
  return (
    <>
      {/* Header — description + search */}
      <div
        className={cn(
          "border-b border-zinc-800 bg-zinc-950 shrink-0",
          isMobile ? "px-4 pt-3 pb-3" : "px-4 pt-3 pb-3"
        )}
      >
        <p
          className={cn(
            "leading-relaxed text-white mb-2.5",
            isMobile ? "text-[14px]" : "text-[13px]"
          )}
        >
          Pick the data you want the agent to use. Selected datasets get
          injected straight into the chat — the agent goes right to them.
        </p>
        <div className="relative">
          <Search
            size={isMobile ? 15 : 13}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              totalSources > 0
                ? `Search ${totalSources} data sources…`
                : "Search data sources…"
            }
            className={cn(
              "w-full pl-8 pr-3 rounded-md",
              "bg-zinc-900 border border-zinc-800",
              "text-white placeholder:text-zinc-500",
              "focus:outline-none focus:ring-1 focus:ring-emerald-400/50 focus:border-emerald-400/50 transition-all",
              isMobile ? "py-2.5 text-[14px]" : "py-1.5 text-[12px]"
            )}
            autoFocus={!isMobile}
          />
        </div>
      </div>

      {/* Filter chip row */}
      <div
        className={cn(
          "border-b border-zinc-800/70 bg-zinc-950 shrink-0",
          isMobile ? "px-3 pt-3 pb-2.5" : "px-3 pt-2.5 pb-2"
        )}
      >
        <div
          className={cn(
            "flex items-center gap-1.5",
            isMobile ? "flex-wrap" : "flex-nowrap overflow-x-auto"
          )}
        >
          <FilterChip
            label="All"
            isActive={activeTags.size === 0}
            onClick={() => setActiveTags(new Set())}
            isMobile={isMobile}
          />
          {FILTER_CHIPS.map((tag) => (
            <FilterChip
              key={tag}
              label={tag}
              isActive={activeTags.has(tag)}
              onClick={() => toggleTag(tag)}
              isMobile={isMobile}
            />
          ))}
        </div>
      </div>

      {/* List */}
      <div
        className="flex-1 min-h-0 overflow-y-auto py-1.5"
        data-testid="data-selector-list"
      >
        {loadError ? (
          <div className="px-4 py-10 text-center">
            <div className="text-[12.5px] font-semibold text-rose-300">
              Couldn’t load data catalog.
            </div>
            <div className="mt-1.5 text-[11.5px] text-zinc-300">{loadError}</div>
          </div>
        ) : !catalog ? (
          <div className="px-4 py-10 flex items-center justify-center gap-2 text-zinc-300">
            <Loader2 className="size-4 animate-spin text-emerald-400" />
            <span className="text-[12.5px] font-medium">Loading…</span>
          </div>
        ) : totalMatches === 0 ? (
          <div className="px-4 py-10 text-center">
            <div className="text-[13px] font-semibold text-white">
              Nothing matches {query ? `“${query}”` : "your filters"}.
            </div>
            <div className="mt-1.5 text-[12px] text-zinc-300 max-w-[80%] mx-auto leading-relaxed">
              Try a different keyword or clear the filter chips above.
            </div>
          </div>
        ) : (
          grouped.map(({ category, items }) => (
            <CategorySection
              key={category}
              category={category}
              items={items}
              selected={selected}
              maxSelected={MAX_SELECTED}
              onToggle={onToggle}
              isMobile={isMobile}
            />
          ))
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-zinc-800 bg-zinc-950 flex items-center justify-between shrink-0">
        <div className="text-[10.5px] text-zinc-400 font-medium">
          {selected.length > 0
            ? "Agent will use only your picks"
            : "No picks — agent will choose its own data"}
        </div>
        <div className="text-[10.5px] font-bold tabular-nums tracking-wider text-emerald-400">
          {selected.length} / {MAX_SELECTED}
        </div>
      </div>
    </>
  );
}

/** Stand-alone selected-data chip row. Rendered by the composer on its
 *  own line below the toolbar on mobile so the toolbar stays clean. */
export function SelectedDataChips({
  selected,
  onRemove,
}: {
  selected: Array<RequestedDataSource>;
  onRemove: (id: string) => void;
}) {
  if (selected.length === 0) return null;
  return (
    <div className="flex items-center flex-wrap gap-1.5">
      {selected.map((src) => (
        <span
          key={src.id}
          className="bg-emerald-400/10 border border-emerald-400/30 text-emerald-300 h-6 px-2 text-[11px] font-medium inline-flex items-center gap-1 rounded-md"
        >
          <span className="truncate max-w-[140px]">{src.name}</span>
          <button
            type="button"
            onClick={() => onRemove(src.id)}
            className="ml-0.5 inline-flex items-center justify-center size-4 rounded-sm hover:bg-emerald-400/25"
            aria-label={`Remove ${src.name}`}
          >
            <X className="size-3 text-emerald-300" />
          </button>
        </span>
      ))}
    </div>
  );
}

function FilterChip({
  label,
  isActive,
  onClick,
  isMobile = false,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
  isMobile?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "shrink-0 inline-flex items-center rounded-md font-bold uppercase tracking-wider transition-all duration-100",
        // Bigger tap target on mobile (iOS recommends 44px min, but 32px
        // is workable when the row is dense and obviously interactive).
        isMobile
          ? "h-8 px-3 text-[11px]"
          : "h-6 px-2.5 text-[10.5px]",
        isActive
          ? "bg-emerald-400 text-zinc-950 ring-1 ring-emerald-400"
          : "bg-zinc-900 text-zinc-300 ring-1 ring-zinc-800 hover:bg-zinc-800 hover:text-white"
      )}
    >
      {label}
    </button>
  );
}

function CategorySection({
  category,
  items,
  selected,
  maxSelected,
  onToggle,
  isMobile = false,
}: {
  category: string;
  items: DataSource[];
  selected: Array<RequestedDataSource>;
  maxSelected: number;
  onToggle: (source: DataSource) => void;
  isMobile?: boolean;
}) {
  const iconName = CATEGORY_ICON[category];
  const Icon = iconName ? ICON_MAP[iconName] : null;
  return (
    <div className="mb-2 last:mb-0">
      <div className="px-4 pt-2 pb-1.5 flex items-baseline gap-2">
        <div className="flex items-center gap-1.5">
          {Icon ? (
            <Icon
              size={11}
              weight="bold"
              className="text-emerald-400 translate-y-[1px]"
            />
          ) : null}
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-400">
            {category}
          </div>
        </div>
        <div className="text-[10px] font-medium text-zinc-500">
          · {items.length}
        </div>
      </div>
      <div className="flex flex-col gap-1 px-2">
        {items.map((src) => (
          <DataRow
            key={src.id}
            source={src}
            isSelected={selected.some((s) => s.id === src.id)}
            isMobile={isMobile}
            onToggle={() => {
              const already = selected.some((s) => s.id === src.id);
              if (!already && selected.length >= maxSelected) return;
              onToggle(src);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function DataRow({
  source,
  isSelected,
  onToggle,
  isMobile = false,
}: {
  source: DataSource;
  isSelected: boolean;
  onToggle: () => void;
  isMobile?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "group w-full text-left rounded-md transition-all duration-100",
        // Mobile: meaty tap target. Desktop: dense.
        isMobile ? "px-3 py-3" : "px-3 py-2.5",
        isSelected
          ? "bg-emerald-400/15 ring-1 ring-emerald-400/60"
          : "hover:bg-zinc-900 active:bg-zinc-900"
      )}
      data-testid={`data-row-${source.id}`}
    >
      {/* Row 1: title (left)  |  checkmark (right) */}
      <div className="flex items-center gap-2">
        <div className={cn(
          "font-semibold leading-tight truncate flex-1",
          isMobile ? "text-[14px]" : "text-[13px]",
          isSelected ? "text-emerald-200" : "text-white",
        )}>
          {source.name}
        </div>
        {isSelected ? (
          <span className={cn(
            "flex items-center justify-center rounded-sm bg-emerald-400 text-zinc-950 shrink-0",
            isMobile ? "size-5" : "size-4"
          )}>
            <Check className={isMobile ? "size-3.5" : "size-3"} strokeWidth={3} />
          </span>
        ) : (
          <span className={cn(
            "rounded-sm border border-zinc-700 group-hover:border-zinc-500 shrink-0",
            isMobile ? "size-5" : "size-4"
          )} />
        )}
      </div>
      {/* Row 2: description — bigger, white-ish, easy to read */}
      <div className={cn(
        "mt-1.5 leading-relaxed text-zinc-200",
        isMobile ? "text-[13px]" : "text-[12px]"
      )}>
        {source.description}
      </div>
    </button>
  );
}

export default DataSelector;
